/**
 * Translation and content helpers for the agreement transfer progress stepper.
 */
import assertNever from 'shared/utils/assertNever';
import { TransferProgressStepStatus } from '../TransferProgressStep/TransferProgressStep';
import { StepDefinition } from './transfer-progress-steps';

export type TranslateFn = (key: string, defaultValue?: string) => string;

/** Resolves the display label for a step, preferring an active-state override when available. */
export function getStepLabel(
  t: TranslateFn,
  stepKey: string,
  status: TransferProgressStepStatus,
): string {
  if (status === 'active') {
    const activeKey = `progress.steps.${stepKey}.activeLabel`;
    return t(activeKey, t(`progress.steps.${stepKey}.label`));
  }
  return t(`progress.steps.${stepKey}.label`);
}

/** Resolves the description text for a step based on its status and optional sub-state. */
export function getStepDescription(
  t: TranslateFn,
  stepKey: string,
  subState: string | undefined,
  status: TransferProgressStepStatus,
): string | null {
  switch (status) {
    case 'complete':
      return t(`progress.steps.${stepKey}.completedDescription`);
    case 'active': {
      const subStateSuffix = subState ? `.${subState}` : '';
      const descKey = `progress.steps.${stepKey}${subStateSuffix}.description`;
      return t(descKey, t(`progress.steps.${stepKey}.description`, ''));
    }
    case 'not started':
      return t(`progress.steps.${stepKey}.pendingDescription`, '') || null;
    default:
      return assertNever(status);
  }
}

/**
 * Bullets are conditional by each step, substate, OR status.
 *   Their translation keys will exist or not in the step definition.
 * Attempt to find bullets and return them as a fully translated array.
 */
export function getStepBullets(
  t: TranslateFn,
  step: StepDefinition,
  subState: string | undefined,
  status: TransferProgressStepStatus,
): string[] {
  switch (status) {
    case 'active': {
      const subStateBullets = subState ? step.subStateBullets?.[subState] : undefined;
      const bullets = subStateBullets ?? step.activeBullets ?? [];
      if (bullets.length === 0) return [];
      const subStateSuffix = subStateBullets ? `.${subState}` : '';
      const bulletPrefix = `progress.steps.${step.key}${subStateSuffix}`;
      return bullets.map((bKey) => t(`${bulletPrefix}.${bKey}`));
    }
    case 'complete':
      return (step.completedBullets ?? []).map((bKey) => t(`progress.steps.${step.key}.${bKey}`));
    case 'not started':
      // Bullets are only used for active or complete steps; not started shows "Awaiting N completed" text instead.
      return [];
    default:
      return assertNever(status);
  }
}
